import { forwardRef, ImgHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

export interface AvatarProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src?: string | null
  name?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

const sizeStyles = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-sm',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, src, name = '', size = 'md', alt, ...props }, ref) => {
    const initials = getInitials(name)

    if (src) {
      return (
        <img
          src={src}
          alt={alt || name}
          className={cn(
            'rounded-full object-cover',
            sizeStyles[size],
            className
          )}
          {...props}
        />
      )
    }

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-full bg-mint-100 flex items-center justify-center font-semibold text-mint-700',
          sizeStyles[size],
          className
        )}
      >
        {initials}
      </div>
    )
  }
)

Avatar.displayName = 'Avatar'
